import { useEffect, useState, useCallback } from 'react';
import { Flex, Button, Text, ResponsiveAcuityInfoPanel, theme } from '../../../2022-component-library';
import { kaysOptotypes } from '../../../helpers/kaysOptotypes';
import { useTestContext } from '../../../context/testContext';
import { useLogmarData, useOptotypeData } from '../../../helpers/reducers';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useLogmarSizeCalculations } from '../../../helpers/useLogmarSizeCalculations';
import { useCalibration } from '../../../context/calibrationContext';
import { useMediaQuery } from '../../../helpers/useMediaQuery';

export const MultipleOptician = ({ eyeTested, goToTestResults }) => {
  const postMessage = usePostMessage();
  const receivedMessage = useReceiveMessage();
  const [valuesInitialised, setValuesInitialised] = useState(false);
  const { getContentSize } = useLogmarSizeCalculations();
  const smallScreen = useMediaQuery('(max-width: 700px)');
  const {
    calibrationData: { distanceFromScreen, methodSelected },
  } = useCalibration();
  const {
    testData: { optotypesToShow, kaysCrowding, testMode: testModeFromTestData, eyeTested: initialLogmarSize, maxPermittedLogmar },
    updateTestData,
  } = useTestContext();
  const {
    currentLogmarData: { logmarSize, finalScore },
    updateLogmarData,
  } = useLogmarData();
  const {
    currentOptotypeData: { currentOptotypes, highlightedOptotype },
    updateOptotypeData,
  } = useOptotypeData();
  const [testMode, setTestMode] = useState(methodSelected !== 'manual' ? 'auto' : testModeFromTestData);

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const changeTestMode = (selectedTestMode) => {
    if (selectedTestMode !== testMode) {
      changeTestModeData(selectedTestMode);
    }
  };

  const changeTestModeData = (selectedTestMode) => {
    updateTestData({ testMode: selectedTestMode });
    setTestMode(selectedTestMode);
    postMessage({
      type: 'updateTestMode',
      content: {
        testMode: selectedTestMode,
      },
    });
  };

  const getRandomOptotypes = useCallback(() => {
    const optotypeKeys = ['apple', 'boot', 'car', 'duck', 'house', 'star'];
    const desiredAmount = optotypesToShow;
    const randomOptotypes = optotypeKeys.sort(() => 0.5 - Math.random()).slice(0, desiredAmount);
    postMessage({ type: 'useOptotypeReducer', action: 'SET_CURRENT_OPTOTYPES', payload: randomOptotypes });
  }, [optotypesToShow, postMessage]);

  const finishTest = () => {
    updateTestData({ [eyeTested]: { score: finalScore } });
    return goToTestResults();
  };

  const handleAnswer = (numberSeen) => {
    const amountToSubtractFromScore = optotypesToShow > 1 ? numberSeen * 0.02 : 0.1;
    postMessage({ type: 'useLogmarReducer', action: 'adjustFinalScore', payload: amountToSubtractFromScore });
    if (logmarSize.id === 13) {
      return finishTest();
    } else {
      postMessage({ type: 'useLogmarReducer', action: 'decrementLogmarSize' });
      return getRandomOptotypes();
    }
  };

  useEffect(() => {
    //this receives "reducer" messages and dispatches the appropriate action.
    if (receivedMessage.type === 'useOptotypeReducer') {
      updateOptotypeData({ type: receivedMessage.action, payload: receivedMessage.payload });
    }
    if (receivedMessage.type === 'useLogmarReducer') {
      updateLogmarData({ type: receivedMessage.action, payload: receivedMessage.payload });
    }
  }, [receivedMessage, updateOptotypeData, updateLogmarData]);

  useEffect(() => {
    if (valuesInitialised) return;
    //initialises values on component load and sends start msg to patient
    postMessage({
      type: 'kaysMultiple',
      content: {
        distanceFromScreen: distanceFromScreen,
        methodSelected: methodSelected,
        testMode: testMode,
      },
    });
    postMessage({ type: 'useLogmarReducer', action: 'setInitialLogmarSize', payload: initialLogmarSize });
    postMessage({ type: 'useOptotypeReducer', action: 'SET_OPTOTYPE_CROWDING', payload: kaysCrowding === 'crowded' });
    getRandomOptotypes();
    setValuesInitialised(true);
  }, [getRandomOptotypes, postMessage, initialLogmarSize, kaysCrowding, distanceFromScreen, methodSelected, testMode, valuesInitialised]);

  useEffect(() => {
    //updates size for patient.
    const newSize = Math.round(getContentSize(logmarSize.id, distanceFromScreen));
    postMessage({ type: 'useOptotypeReducer', action: 'SET_OPTOTYPE_SIZE', payload: newSize });
  }, [distanceFromScreen, logmarSize.id, postMessage, getContentSize]);

  const highlightOptotype = (optotype) => {
    return postMessage({ type: 'useOptotypeReducer', action: 'SET_HIGHLIGHTED_OPTOTYPE', payload: optotype });
  };

  return (
    <Flex centered column width='85vw' maxWidth='700px'>
      <Flex width='100%' justifyContent='space-between' alignItems={!smallScreen && 'center'}>
        <Flex
          column
          centered
          border={`1px solid ${theme.colors.neutralLight}`}
          borderRadius='4px'
          width='55vw'
          height='50vh'
          maxHeight='400px'
          marginBottom='1rem'
          marginRight='1rem'
        >
          <Flex width='100%' centered>
            {currentOptotypes.map((opt, idx) => {
              const previewSize = Math.min(Math.floor(100 / optotypesToShow), 70);
              return (
                <Flex
                  key={`optotype-${opt}`}
                  onClick={() => (optotypesToShow > 1 ? highlightOptotype(opt) : null)}
                  style={{
                    cursor: optotypesToShow > 1 ? 'pointer' : 'auto',
                    backgroundColor: highlightedOptotype === opt ? '#90ee90' : 'transparent',
                    position: 'relative',
                    ...(kaysCrowding === 'open' && { margin: previewSize / 2 }),
                    ...(kaysCrowding === 'crowded' && {
                      borderStyle: 'solid',
                      borderTopWidth: '5px',
                      borderRightWidth: currentOptotypes.length < 2 || idx > 3 ? '5px' : 0,
                      borderBottomWidth: '5px',
                      borderLeftWidth: '5px',
                      padding: '1rem',
                    }),
                  }}
                >
                  <Flex height={previewSize + 'px'} width={previewSize + 'px'}>
                    {kaysOptotypes[opt]}
                  </Flex>
                  {highlightedOptotype === opt && (
                    <Button
                      onClick={() => highlightOptotype(null)}
                      style={{
                        position: 'absolute',
                        top: previewSize * 3,
                        left: '50%',
                        width: '2.5em',
                        padding: '0.25rem 0.5rem',
                        background: theme.colors.negativeDark,
                        borderColor: theme.colors.negativeDark,
                        transform: 'translateX(-50%)',
                      }}
                    >
                      {'\u00d7'}
                    </Button>
                  )}
                </Flex>
              );
            })}
          </Flex>
          {optotypesToShow > 1 ? (
            <>
              <Text fontSize='0.825rem' marginTop='2rem'>{`OPTOTYPES SEEN`}</Text>
              <Flex width='75%' margin='0.25rem 0' justifyContent='space-evenly'>
                {[1, 2, 3, 4, 5].map((num) => {
                  return (
                    <Button key={`answer-btn-${num}`} aspectRatio='1/1' width='30px' padding='0px' onClick={() => handleAnswer(num)}>
                      {num}
                    </Button>
                  );
                })}
              </Flex>
            </>
          ) : (
            <Button width='15em' maxWidth='30vw' onClick={() => handleAnswer()} marginTop='1rem' backgroundColor={theme.colors.secondaryDark}>
              {'Correct'}
            </Button>
          )}
        </Flex>
        <Flex column height='50vh' maxHeight='200px' width='40vw' justifyContent='space-between' alignItems='center'>
          <Flex centered borderBottom='1px solid black' width='100%' padding='0.25rem 0'>
            <Text>{`logMAR Size: ${logmarSize?.value?.toFixed(1) || 0}`}</Text>
          </Flex>
          <Flex flexWrap='wrap' alignItems='center' justifyContent='space-evenly'>
            <Button
              width='15em'
              maxWidth='30vw'
              disabled={logmarSize.id === 1 || logmarSize.id === maxPermittedLogmar.id}
              onClick={() => {
                postMessage({ type: 'useLogmarReducer', action: 'incrementLogmarSize' });
                return getRandomOptotypes();
              }}
            >
              {'Previous logMAR'}
            </Button>
          </Flex>
          <Button width='15em' maxWidth='30vw' backgroundColor={theme.colors.negativeDark} onClick={() => finishTest()}>
            {`${optotypesToShow > 1 ? 'Zero Seen /' : ''} End Test`}
          </Button>
          <Button width='15em' maxWidth='30vw' backgroundColor={theme.colors.callToAction} onClick={() => getRandomOptotypes()}>
            Repeat
          </Button>
        </Flex>
      </Flex>
      <ResponsiveAcuityInfoPanel changeTestMode={changeTestMode} />
    </Flex>
  );
};

export default MultipleOptician;
